/** Shared PLAN page state passed to every setup section. */
import { createContext, useContext } from 'react';
import type { EpochMs, Id, LocationKind, MissionBundle } from '@apos/shared';
import { toast } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import type { SelectionRef } from '../../lib/types';

/** What a map tap does right now. */
export type Placing =
  | { kind: 'location'; locationKind: LocationKind; id?: Id }
  | { kind: 'camera'; id: Id }
  | { kind: 'route_waypoint'; aircraftId: Id }
  | { kind: 'center' };

export interface PlanCtx {
  bundle: MissionBundle;
  canEdit: boolean;
  isOwner: boolean;
  placing: Placing | null;
  setPlacing(p: Placing | null): void;
  selection: SelectionRef | null;
  select(s: SelectionRef | null): void;
  sunT: EpochMs;
  setSunT(t: EpochMs): void;
  /** Re-fetch the bundle after a mutation (the WebSocket also announces changes). */
  refresh(): Promise<void>;
  /** Run a mutation with error toast + refresh; returns the result or undefined on failure. */
  mutate<T>(label: string, fn: () => Promise<T>): Promise<T | undefined>;
}

export const PlanContext = createContext<PlanCtx | null>(null);

export function usePlan(): PlanCtx {
  const c = useContext(PlanContext);
  if (!c) throw new Error('usePlan outside PlanContext');
  return c;
}

export async function runMutation<T>(label: string, fn: () => Promise<T>, refresh: () => Promise<void>): Promise<T | undefined> {
  try {
    const r = await fn();
    void refresh();
    return r;
  } catch (e) {
    toast({ title: `Could not ${label}`, body: errorMessage(e), tone: 'alert' });
    return undefined;
  }
}
